import { Link } from 'react-router-dom';
import { X, Plus, Minus, Trash2, ShoppingBag, AlertCircle } from 'lucide-react';
import { useCart } from '../context/CartContext';

export default function CartDrawer() {
  const { cartItems, cartOpen, setCartOpen, removeFromCart, updateQuantity, cartTotal, cartCount } = useCart();

  return (
    <>
      {/* Backdrop */}
      {cartOpen && (
        <div
          data-testid="cart-backdrop"
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] transition-opacity"
          onClick={() => setCartOpen(false)}
        />
      )}

      {/* Drawer */}
      <div
        data-testid="cart-drawer"
        className={`fixed top-0 right-0 h-full w-full sm:w-[420px] bg-[#111111] border-l border-white/10 z-[70] flex flex-col transform transition-transform duration-300 ${
          cartOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <ShoppingBag size={20} className="text-orange-500" />
            <span className="font-bebas text-xl tracking-widest text-white">
              YOUR CART
              {cartCount > 0 && (
                <span className="ml-2 font-inter text-sm text-orange-500">({cartCount})</span>
              )}
            </span>
          </div>
          <button
            data-testid="cart-close-button"
            onClick={() => setCartOpen(false)}
            className="text-zinc-400 hover:text-white transition-colors p-1"
          >
            <X size={22} />
          </button>
        </div>

        {/* NFA Notice */}
        {cartItems.some(i => i.product.nfaItem) && (
          <div className="mx-4 mt-4 flex items-start gap-2 bg-orange-500/10 border border-orange-500/30 p-3">
            <AlertCircle size={15} className="text-orange-500 flex-shrink-0 mt-0.5" />
            <p className="font-inter text-xs text-orange-300 leading-relaxed">
              <strong>NFA Notice:</strong> Your cart contains NFA items. All federal NFA rules apply. Please select UPS shipping at checkout.
            </p>
          </div>
        )}

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {cartItems.length === 0 ? (
            <div data-testid="cart-empty" className="flex flex-col items-center justify-center h-64 text-center">
              <ShoppingBag size={48} className="text-zinc-700 mb-4" />
              <p className="font-bebas text-xl text-zinc-500 tracking-widest">YOUR CART IS EMPTY</p>
              <p className="font-inter text-sm text-zinc-600 mt-2">Add some products to get started</p>
              <button
                data-testid="cart-continue-shopping"
                onClick={() => setCartOpen(false)}
                className="mt-6 font-bebas text-sm tracking-widest text-orange-500 border border-orange-500 px-4 py-2 hover:bg-orange-500 hover:text-white transition-colors"
              >
                SHOP NOW
              </button>
            </div>
          ) : (
            cartItems.map(item => (
              <div
                key={item.key}
                data-testid={`cart-item-${item.product.id}`}
                className="flex gap-3 bg-[#1a1a1a] border border-white/5 p-3"
              >
                <div className="w-16 h-16 bg-[#0a0a0a] flex-shrink-0 overflow-hidden">
                  <img
                    src={item.product.image}
                    alt={item.product.name}
                    className="w-full h-full object-contain p-1"
                    onError={e => { e.target.style.opacity = '0.3'; }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-inter text-sm font-medium text-white leading-tight">{item.product.name}</p>
                      {item.variant && (
                        <p className="font-inter text-xs text-zinc-500 mt-0.5">{item.variant}</p>
                      )}
                      {item.product.nfaItem && (
                        <span className="inline-block font-inter text-[10px] text-orange-500 uppercase tracking-wider mt-0.5">NFA Item</span>
                      )}
                    </div>
                    <button
                      data-testid={`remove-item-${item.product.id}`}
                      onClick={() => removeFromCart(item.key)}
                      className="text-zinc-600 hover:text-red-500 transition-colors flex-shrink-0"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-1 border border-white/10">
                      <button
                        data-testid={`decrease-qty-${item.product.id}`}
                        onClick={() => updateQuantity(item.key, item.quantity - 1)}
                        className="p-1.5 text-zinc-400 hover:text-white transition-colors"
                      >
                        <Minus size={12} />
                      </button>
                      <span className="font-inter text-sm text-white w-8 text-center">{item.quantity}</span>
                      <button
                        data-testid={`increase-qty-${item.product.id}`}
                        onClick={() => updateQuantity(item.key, item.quantity + 1)}
                        className="p-1.5 text-zinc-400 hover:text-white transition-colors"
                      >
                        <Plus size={12} />
                      </button>
                    </div>
                    <span className="font-bebas text-lg text-white tracking-wide">
                      ${(item.product.price * item.quantity).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {cartItems.length > 0 && (
          <div className="border-t border-white/10 px-6 py-6 space-y-4">
            {/* Free shipping note */}
            {cartTotal < 450 && (
              <div className="bg-[#1e1e1e] border border-white/5 p-3">
                <p className="font-inter text-xs text-zinc-400 text-center">
                  Spend <span className="text-orange-500 font-semibold">${(450 - cartTotal).toFixed(2)}</span> more for free shipping
                </p>
                <div className="mt-2 h-1 bg-[#27272a]">
                  <div
                    className="h-full bg-orange-500 transition-all duration-300"
                    style={{ width: `${Math.min((cartTotal / 450) * 100, 100)}%` }}
                  />
                </div>
              </div>
            )}
            {cartTotal >= 450 && (
              <div className="bg-green-500/10 border border-green-500/30 p-2 text-center">
                <span className="font-inter text-xs text-green-400">Free shipping unlocked!</span>
              </div>
            )}

            <div className="flex items-center justify-between">
              <span className="font-inter text-sm text-zinc-400 uppercase tracking-wider">Subtotal</span>
              <span data-testid="cart-total" className="font-bebas text-2xl text-white tracking-wide">${cartTotal.toFixed(2)}</span>
            </div>
            <p className="font-inter text-xs text-zinc-600">Taxes and shipping calculated at checkout</p>
            <Link
              to="/checkout"
              data-testid="checkout-button"
              onClick={() => setCartOpen(false)}
              className="w-full flex items-center justify-center bg-orange-500 hover:bg-orange-600 text-white font-bebas text-lg tracking-widest py-4 transition-colors"
            >
              PROCEED TO CHECKOUT
            </Link>
            <button
              data-testid="continue-shopping-button"
              onClick={() => setCartOpen(false)}
              className="w-full border border-white/20 hover:border-white/40 text-white font-bebas text-sm tracking-widest py-3 transition-colors"
            >
              CONTINUE SHOPPING
            </button>
          </div>
        )}
      </div>
    </>
  );
}
